import { FC, useState, useEffect } from 'react'
import { ApplicationFrame } from '../../components/ApplicationFrame'
import justEatLogoAsset from '../../assets/just-eat/logo.png'
import {
  useClientApiCall,
  useClientApiMutation,
} from '../../hooks/useClientApiCall'
import { useSearchParams } from 'react-router-dom'
import {
  Box,
  Button,
  Grid,
  Typography,
  TextField,
  FormControl,
  Switch,
  CircularProgress,
  Autocomplete,
  Alert,
  AlertTitle,
  Select,
  MenuItem,
  Card,
  InputAdornment,
  Container,
} from '@mui/material'

import { ApplicationLoadingScreen } from '../../components/ApplicationLoadingScreen'
import { toast } from 'react-toastify'
import { set } from 'date-fns'
/**
 * Just Eat Application
 */

export const JustEat: FC = () => {
  const [params] = useSearchParams()
  const session = params.get('session')!

  const connection = useClientApiCall<any>(session, 'connection')
  const doConnect = useClientApiMutation(session, 'connect')

  /**
   * Derived State
   */
  const isApiKeyConfigured = connection.data?.connection?.justEatStoreId

  /**
   * While we are loading the connection, we will show a loading spinner
   */
  if (connection.isLoading) {
    return <ApplicationLoadingScreen />
  }

  // When the user clicks the connect button, we will call this function
  const onConnect = (
    merchantName: string,
    url: string,
    justEatStoreId: string,
    address: string,
    setLoading: any
  ) => {
    setLoading(true)
    doConnect.mutate(
      { merchantName, url, justEatStoreId, address },
      {
        onSuccess: () => {
          setLoading(false)
          connection.refetch()
        },
      }
    )
  }

  /**
   *
   */
  return (
    <ApplicationFrame heroImage={justEatLogoAsset} backgroundColor="#ff8100">
      {/* Show the API Key dialog */}
      {!isApiKeyConfigured && <ConfigureApiCredentials onConnect={onConnect} />}

      {/* Show the configured view */}
      {isApiKeyConfigured && <ConfigureIntegration />}
    </ApplicationFrame>
  )
}

export const ConfigureApiCredentials: FC<{
  onConnect: (
    merchantName: string,
    url: string,
    justEatStoreId: string,
    address: string,
    setLoading: any
  ) => void
}> = (props) => {
  const [merchantName, setMerchantName] = useState('')
  const [justEatStoreId, setJustEatStoreId] = useState('')
  const [address, setAddress] = useState('')
  const [url, setUrl] = useState('')

  const [loading, setLoading] = useState(false)

  // Postcode search states
  const [postcode, setPostcode] = useState('')
  const [restaurants, setRestaurants] = useState<any[]>([])
  const [loadingRestaurants, setLoadingRestaurants] = useState(false)
  const [showManualForm, setShowManualForm] = useState(false)
  const [selectedRestaurant, setSelectedRestaurant] = useState<any>(null)
  const [isConfirming, setIsConfirming] = useState(false)

  const [params] = useSearchParams()
  const session = params.get('session')!
  const fetchRestaurants = useClientApiMutation<any>(
    session,
    'restaurants-by-postcode'
  )

  // Fetch restaurants when postcode changes
  useEffect(() => {
    if (postcode.length >= 5) {
      setLoadingRestaurants(true)
      setSelectedRestaurant(null)
      fetchRestaurants.mutate(
        { postcode },
        {
          onSuccess: (data) => {
            setLoadingRestaurants(false)
            setRestaurants(data.restaurants || [])
          },
          onError: () => {
            setLoadingRestaurants(false)
            setRestaurants([])
          },
        }
      )
    } else {
      setRestaurants([])
      setSelectedRestaurant(null)
    }
  }, [postcode])

  // Handle restaurant selection
  const handleRestaurantSelect = (restaurant: any) => {
    setSelectedRestaurant(restaurant)
    setMerchantName(restaurant.name)
    setJustEatStoreId(restaurant.id)
    setUrl(restaurant.url || '')
    setAddress(restaurant.address)
    setIsConfirming(true)
  }

  // Handle manual form toggle
  const toggleManualForm = () => {
    setShowManualForm(!showManualForm)
    setPostcode('')
    setUrl('')
    setRestaurants([])
    setSelectedRestaurant(null)
    setIsConfirming(false)
    setMerchantName('')
    setJustEatStoreId('')
    setAddress('')
  }

  // Handle confirmation
  const handleConfirm = () => {
    props.onConnect(merchantName, url, justEatStoreId, address, setLoading)
  }

  // Clear form
  const handleClearForm = () => {
    setMerchantName('')
    setJustEatStoreId('')
    setUrl('')
    setAddress('')
    setPostcode('')
    setRestaurants([])
    setSelectedRestaurant(null)
    setIsConfirming(false)
    setShowManualForm(false)
  }

  const isFormDisabled = !justEatStoreId || !merchantName || !address

  return (
    <Box pb={2}>
      <Grid container spacing={3} p={2}>
        {/* Instructions */}
        <Grid item sm={12}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Search for restaurants by postcode, or enter your information
            manually below.
          </Typography>
        </Grid>

        {/* Postcode Search Section */}
        {!showManualForm && (
          <>
            <Grid item sm={12}>
              <TextField
                label="Enter Postcode"
                fullWidth
                value={postcode}
                onChange={(e) => setPostcode(e.target.value)}
                placeholder="e.g., WS4 2AR"
                helperText="Enter postcode to find restaurants"
              />
            </Grid>

            {/* Loading State */}
            {loadingRestaurants && (
              <Grid item sm={12}>
                <Box display="flex" alignItems="center" gap={2}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Searching...</Typography>
                </Box>
              </Grid>
            )}

            {/* Restaurant Selection */}
            {!loadingRestaurants && restaurants.length > 0 && (
              <Grid item sm={12}>
                <Autocomplete
                  options={restaurants}
                  getOptionLabel={(option) => option.name}
                  onChange={(event, newValue) => {
                    if (newValue) handleRestaurantSelect(newValue)
                  }}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      sx={{ display: 'flex', alignItems: 'center', gap: 2 }}
                    >
                      {option.logo && (
                        <img
                          src={option.logo}
                          alt={option.name}
                          style={{
                            width: 40,
                            height: 40,
                            borderRadius: 4,
                            objectFit: 'cover',
                          }}
                        />
                      )}
                      <Box>
                        <Typography variant="body1" fontWeight="medium">
                          {option.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {option.address}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Restaurant"
                      placeholder="Type to search restaurants..."
                      helperText={`Found ${restaurants.length} restaurants. Select one to continue.`}
                    />
                  )}
                  noOptionsText="No restaurants found"
                />
              </Grid>
            )}
            {/* Manual Entry Link */}
            <Grid item sm={12}>
              <Button
                variant="text"
                onClick={toggleManualForm}
                sx={{ textTransform: 'none', p: 0 }}
              >
                <Typography variant="body2" color="primary">
                  Enter Manually
                </Typography>
              </Button>
            </Grid>

            {/* No Results Message */}
            {!loadingRestaurants &&
              postcode.length >= 5 &&
              restaurants.length === 0 && (
                <Grid item sm={12}>
                  <Typography variant="body2" color="text.secondary">
                    No restaurants found for this postcode. Try a different
                    postcode or use manual entry.
                  </Typography>
                </Grid>
              )}
          </>
        )}

        {/* Manual Form Fields */}
        {showManualForm && (
          <>
            <Grid item sm={12}>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Typography variant="body2">Manual Entry Mode</Typography>
                <Button
                  variant="text"
                  onClick={toggleManualForm}
                  size="small"
                  sx={{ textTransform: 'none' }}
                >
                  <Typography variant="body2" color="primary">
                    Back to Postcode Search
                  </Typography>
                </Button>
              </Box>
            </Grid>

            <Grid item sm={12}>
              <TextField
                label="Restaurant Name"
                fullWidth
                value={merchantName}
                onChange={(event) => setMerchantName(event.target.value)}
                required
                placeholder="Restaurant name"
              />
            </Grid>

            <Grid item sm={12}>
              <TextField
                fullWidth
                label="Store ID"
                value={justEatStoreId}
                onChange={(event) => setJustEatStoreId(event.target.value)}
                required
                placeholder="Just Eat store ID"
              />
            </Grid>

            <Grid item sm={12}>
              <TextField
                fullWidth
                label="Address"
                value={address}
                onChange={(event) => setAddress(event.target.value)}
                required
                placeholder="Restaurant address"
                multiline
                rows={2}
              />
            </Grid>
          </>
        )}

        {/* Confirmation Form (when restaurant selected from postcode) */}
        {isConfirming && selectedRestaurant && (
          <>
            <Grid item sm={12}>
              <Card variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                <Typography variant="h6" gutterBottom>
                  Confirm Restaurant Details
                </Typography>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  {selectedRestaurant.image && (
                    <img
                      src={selectedRestaurant.image}
                      alt={selectedRestaurant.name}
                      style={{
                        width: 60,
                        height: 60,
                        borderRadius: 8,
                        objectFit: 'cover',
                      }}
                    />
                  )}
                  <Box>
                    <Typography variant="body1">
                      {selectedRestaurant.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedRestaurant.address}
                    </Typography>
                  </Box>
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Please review the details above. You can edit them if needed.
                </Typography>

                <TextField
                  label="Restaurant Name"
                  fullWidth
                  value={merchantName}
                  onChange={(event) => setMerchantName(event.target.value)}
                  required
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Store ID"
                  value={justEatStoreId}
                  onChange={(event) => setJustEatStoreId(event.target.value)}
                  required
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Address"
                  value={address}
                  onChange={(event) => setAddress(event.target.value)}
                  required
                  multiline
                  rows={2}
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Website URL"
                  value={url}
                  onChange={(event) => setUrl(event.target.value)}
                  placeholder="https://www.just-eat.co.uk/restaurant-name"
                  required
                />
              </Card>
            </Grid>
          </>
        )}

        {/* Action Buttons */}
        <Grid item sm={12}>
          <Box display="flex" gap={2} alignItems="center">
            {isConfirming ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleConfirm}
                disabled={isFormDisabled || loading}
              >
                {loading && (
                  <CircularProgress style={{ marginRight: '1em' }} size={18} />
                )}
                Confirm Connection
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  props.onConnect(
                    merchantName,
                    url,
                    justEatStoreId,
                    address,
                    setLoading
                  )
                }
                disabled={isFormDisabled || loading}
              >
                {loading && (
                  <CircularProgress style={{ marginRight: '1em' }} size={18} />
                )}
                Connect
              </Button>
            )}

            <Button
              variant="outlined"
              onClick={handleClearForm}
              disabled={!merchantName && !justEatStoreId && !address}
            >
              Clear
            </Button>
          </Box>
        </Grid>

        {/* Connection Notice */}
        <Grid item sm={12}>
          <Alert severity="info" sx={{ mt: 2 }}>
            <AlertTitle>Connection Timeline</AlertTitle>
            <Typography variant="body2">
              It could take up to 7 days for the connection to be established.
              Our support team will reach out to you with an exact ETA.
            </Typography>
          </Alert>
        </Grid>
      </Grid>
    </Box>
  )
}
export const ConfigureIntegration: FC = () => {
  const [params] = useSearchParams()
  const session = params.get('session')!
  const connection = useClientApiCall<any>(session, 'connection')
  const updateConnection = useClientApiMutation(session, 'connection')
  const [loading, setLoading] = useState(false)
  const menuSyncMutation = useClientApiMutation<any>(
    params.get('session')!,
    'publish-menu',
    'PUT'
  )

  // Get store info from connection
  const connectedStore = {
    name:
      connection.data?.connection?.merchantName ||
      'Blue Burger - FH Doordash Location',
    storeId: connection.data?.connection?.justEatStoreId || 'N/A',
    address:
      connection.data?.connection?.address ||
      '1033 W Roosevelt Way, Tempe, AZ 85288, USA',
    image:
      connection.data?.connection?.storeImageUrl ||
      '/assets/logos/justeat_banner.jpg',
    webUrl: connection.data?.connection?.url || 'https://www.just-eat.co.uk/',
    selectedMenuInfo:
      connection.data?.menuList.find(
        (menu: any) => menu.id === connection.data?.connection?.selectedMenuId
      ) || null,
  }
  console.log('Connected Store:', connection.data?.connection)
  const [isStoreOnline, setIsStoreOnline] = useState(
    connection.data?.connection?.isStoreOnline
  )
  const [hikePercentage, setPriceHikePercentage] = useState(
    connection.data?.connection?.priceHikePercent || 0
  )
  const [isRoundOffEnabled, setRoundOffEnabled] = useState(
    connection.data?.connection?.roundOff !== false
  )
  const [isPriceHike, setIsPriceHike] = useState(
    connection.data?.connection?.isPriceHike !== false
  )
  const [selectedMenuId, setSelectedMenuId] = useState('')

  // Get menu list from connection data
  const menuList = connection.data?.menuList || []

  const handleStoreStatusChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    console.log(event)
    setIsStoreOnline(event.target.value)
    updateConnection.mutate({
      isStoreOnline: event.target.value,
    })
  }

  const handleHikePercentage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const hikePercent = event.target.value
    if (/^\d*\.?\d{0,2}$/.test(hikePercent) && !/[a-zA-Z]/.test(hikePercent)) {
      const numericValue = parseFloat(hikePercent)
      if (hikePercent === '' || numericValue > 0) {
        setPriceHikePercentage(hikePercent)
      }
    }
  }

  const handlePriceHikeUpdate = () => {
    if (hikePercentage > 0) {
      updateConnection.mutate({ priceHikePercent: +hikePercentage })
    }
  }

  const handlerIsRoundOffEnabled = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRoundOffEnabled(event.currentTarget.checked)
    updateConnection.mutate({ roundOff: event.currentTarget.checked })
  }

  const handlerIsPriceHike = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsPriceHike(event.currentTarget.checked)
    updateConnection.mutate({ isPriceHike: event.currentTarget.checked })
  }

  const handleMenuPublish = () => {
    if (selectedMenuId) {
      setLoading(true)
      menuSyncMutation.mutate(
        { selectedMenuId },
        {
          onSuccess: (res) => {
            console.log(res)
            if (res.error) {
              toast.error(
                'Error publishing menu: ' + res.error ||
                  'Failed to publish menu.'
              )
            } else {
              toast.success('Menu Sync Initiated!')
            }
          },
          onError: (error: any) => {
            toast.error(
              error?.response?.data?.message || 'Failed to publish menu.'
            )
          },
          onSettled: () => {
            setLoading(false)
          },
        }
      )
    }
  }

  const handleDisconnect = () => {
    // TODO: Implement disconnect logic
    //toast.info('Disconnected!')
  }

  return (
    <Box sx={{ p: 0, bgcolor: '#f5f5f5', minHeight: '100vh', margin: 0 }}>
      <Container>
        <Grid container spacing={2}>
          {/* First Card: Connected Store Details */}
          <Grid item xs={12}>
            <Card sx={{ p: 3, borderRadius: 2, boxShadow: 2 }}>
              <Typography
                variant="h4"
                gutterBottom
                sx={{ textAlign: 'center', mb: 3 }}
              >
                Connected Store Details
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>Store Name:</strong> {connectedStore.name}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Store Address:</strong> {connectedStore.address}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <a
                  href={connectedStore.webUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  <Button
                    variant="outlined"
                    sx={{
                      borderColor: '#4caf50',
                      color: '#4caf50',
                      textTransform: 'none',
                      px: 3,
                      py: 1,
                    }}
                  >
                    VIEW MENU
                  </Button>
                </a>
              </Box>

              <Box
                sx={{ display: 'flex', gap: 3, alignItems: 'center', mb: 2 }}
              >
                <FormControl sx={{ minWidth: 200 }}>
                  <Select
                    value={isStoreOnline}
                    onChange={(e) => handleStoreStatusChange(e)}
                    displayEmpty
                    sx={{
                      borderColor: '#ff9800',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#ff9800',
                      },
                    }}
                  >
                    <MenuItem value={false}>Orders Paused</MenuItem>
                    <MenuItem value={true}>Orders Resumed</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: '0.875rem' }}
              >
                <strong>Note:</strong> Using the Pause order feature will close
                your JustEat website and stop you from receiving orders through
                JustEat.
              </Typography>
            </Card>
          </Grid>

          {/* Second Card: Menu and Integration Details */}
          <Grid item xs={12}>
            <Card sx={{ p: 3, borderRadius: 2, boxShadow: 2 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>Latest Published Menu:</strong>{' '}
                  {connectedStore.selectedMenuInfo?.name}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight="medium" gutterBottom>
                  Select Menu
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={selectedMenuId}
                    onChange={(e) => setSelectedMenuId(e.target.value)}
                    displayEmpty
                    sx={{
                      borderColor: '#ff9800',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#ff9800',
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Choose a menu ...</em>
                    </MenuItem>
                    {menuList.map((menu: any) => (
                      <MenuItem key={menu.id} value={menu.id}>
                        {menu.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight="medium" gutterBottom>
                  Enable Price Hike
                </Typography>
                <Switch
                  checked={isPriceHike}
                  onChange={handlerIsPriceHike}
                  color="success"
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    Price Hike Percentage
                  </Typography>
                  <TextField
                    value={hikePercentage}
                    onChange={handleHikePercentage}
                    onBlur={handlePriceHikeUpdate}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">%</InputAdornment>
                      ),
                    }}
                    sx={{ width: '100%' }}
                  />
                </Box>

                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 3 }}
                >
                  <Typography variant="body1">Price Roundoff</Typography>
                  <Switch
                    checked={isRoundOffEnabled}
                    onChange={handlerIsRoundOffEnabled}
                    color="success"
                  />
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: '#4caf50',
                    color: 'white',
                    textTransform: 'none',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      bgcolor: '#45a049',
                    },
                  }}
                  onClick={handleMenuPublish}
                  disabled={!selectedMenuId || loading}
                >
                  PUBLISH MENU
                </Button>

                <Button
                  variant="outlined"
                  sx={{
                    borderColor: '#f44336',
                    color: '#f44336',
                    textTransform: 'none',
                    px: 4,
                    py: 1.5,
                    '&:hover': {
                      borderColor: '#d32f2f',
                      color: '#d32f2f',
                    },
                  }}
                  onClick={handleDisconnect}
                >
                  DISCONNECT INTEGRATION
                </Button>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  )
}
